import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the power-of-attorney-abroad guide. */
const TITLE = "태국 위임장(Power of Attorney) 해외 사용 — 반송되지 않게 작성하는 법";
const DESC =
  "해외에서 사용할 태국 위임장의 작성 방법: 위임 범위 기재, 인증 순서, 그리고 재작성과 반송을 유발하는 대표적인 실수를 정리했습니다.";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "위임장은 변호사가 작성해야 합니까?",
    answer:
      "법으로 강제되지는 않지만 제출 기관은 권한 범위와 형식을 구체적으로 요구하는 경우가 많습니다. 목적지 요건을 아는 변호사가 작성하면 범위 부족이나 모호한 표현으로 반송될 위험이 줄어듭니다.",
  },
  {
    question: "권한 범위는 어느 정도로 써야 합니까?",
    answer:
      "목적 달성에 필요한 범위로 한정하고 항목별로 구체적으로 기재합니다(특정 계약 서명, 특정 서류 수령, 신고 대행 등). 너무 좁으면 일을 못 하고, 너무 넓으면 기관이 의문을 제기하거나 남용 위험이 생깁니다.",
  },
  {
    question: "공증 업무 자격 변호사(Notarial Services Attorney)를 거쳐야 합니까?",
    answer:
      "해외에서 사용하는 위임장은 통상 공증 업무 자격을 갖춘 변호사의 서명 인증 또는 선서 확인을 먼저 받고, 이후 인증 절차로 넘어갑니다.",
  },
  {
    question: "인증 순서는 어떻게 됩니까?",
    answer:
      "일반적으로 변호사 공증 → 태국 외교부 영사국 확인 → 목적지국 주태 대사관·영사관 확인 순서입니다. 순서를 바꾸면 반송되어 다시 진행해야 합니다.",
  },
  {
    question: "위임장 번역이 필요합니까?",
    answer:
      "목적지 요건에 따릅니다. 대부분 목적지 언어 또는 이중 언어 판본을 요구하며, 번역문에는 도장과 부기 문구까지 모두 포함해야 합니다.",
  },
  {
    question: "위임장에 유효기간이 있습니까?",
    answer:
      "본문에서 기간을 정하거나 목적 달성 시 자동 실효되도록 정할 수 있습니다. 정하지 않으면 일부 제출 기관은 서명 시점이 수개월 이내인지를 기준으로 판단합니다.",
  },
  {
    question: "가장 흔한 실수는 무엇입니까?",
    answer:
      "권한 범위가 지나치게 포괄적인 경우, 당사자 정보와 여권 표기 불일치, 서명 인증 또는 서명 페이지 누락, 인증 순서 착오, 그리고 도장 문구 번역 누락입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/power-of-attorney-abroad-guide")({
  component: KoPoaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "태국 위임장, power of attorney 태국, 태국 위임장 공증, 위임장 영사확인, 태국 위임장 번역",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const PARTS: Array<{ part: string; note: string }> = [
  { part: "당사자 정보", note: "위임인과 수임인의 이름 표기·증서 번호가 여권과 일치해야 함" },
  { part: "위임 사항", note: "대행할 행위와 대상을 항목별로 구체적으로 기재" },
  { part: "권한 제한", note: "재위임 금지, 자산 처분 금지 등 제한을 명시" },
  { part: "기간과 실효 조건", note: "유효기간 또는 목적 달성 시 자동 실효를 규정" },
  { part: "준거법과 언어", note: "준거법을 밝히고 어느 언어 판본이 기준인지 지정" },
  { part: "서명과 인증", note: "서명 일자·장소, 공증 변호사의 확인과 직인" },
];

const STEPS: string[] = [
  "제출 기관의 위임장 내용·형식 요건을 서면으로 먼저 확보",
  "필요에 맞춰 위임 사항과 제한 조항을 항목별로 작성",
  "당사자 정보가 여권 표기와 완전히 일치하는지 확인",
  "공증 변호사 앞에서 서명하고 확인·선서 증명을 받음",
  "태국 외교부 영사국 확인을 받음",
  "목적지국 주태 대사관·영사관 확인 후 발송 또는 제출",
];

const MISTAKES: string[] = [
  "위임 사항이 지나치게 포괄적이어서 기관이 권한 범위를 확인할 수 없는 경우",
  "이름 표기와 증서 번호가 여권과 다른 경우",
  "서명 확인이 없거나 서명 페이지가 빠진 경우",
  "인증 순서 착오(영사국 전에 대사관을 먼저 진행)",
  "번역문에서 도장·직인·부기 문구가 누락된 경우",
];

function KoPoaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="ko" />
      <p className="answer-summary speakable-summary">
        해외에서 사용할 태국 위임장에는 당사자 정보, 항목별 위임 사항, 제한 조항, 유효기간을 명시하고 공증 변호사 앞에서 서명 확인을 받아야 합니다.
        이후 태국 외교부 영사국 확인과 목적지국 주태 대사관·영사관 확인을 순서대로 진행합니다.
        권한 범위의 모호함과 인증 순서 착오가 가장 흔한 반송 사유입니다.
      </p>

      <h2>1. 위임장에 담아야 할 항목</h2>
      <ul>
        {PARTS.map((p) => (
          <li key={p.part}>
            <strong>{p.part}:</strong> {p.note}
          </li>
        ))}
      </ul>

      <h2>2. 진행 순서</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 흔한 실수</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 참고 자료와 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          태국 변호사협회
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/notary" className="text-primary underline">
            공증 업무 변호사(Notary Public) 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/corporate" className="text-primary underline">
            회사·비즈니스 법률 서류 서비스
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 검토일 {PUBLISHED}. 본 가이드는 일반 정보이며 개별 사안에 대한 법률 의견이 아닙니다. 진행 전 제출 기관의 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
